import { useContext, useMemo } from 'react';
import { ResponseActionsContext } from '../../context/ResponseActionsContext';
import { ResponseActionsScopeContext } from '../../context/ResponseActionsScopeContext';
import {
  ResponseActionHandler,
  ResponseActionScope,
  ResponseActionsContextType,
} from '../../types/context';

type ScopedResponseActionsContext = {
  isProvided: true;
  registerResponseAction: (
    name: string,
    handler: ResponseActionHandler
  ) => () => void;
  getResponseActionHandler: (name: string) => ResponseActionHandler | undefined;
};

export const useResponseActionsContext = (
  // Bypasses the ScopeContext lookup — same idea as useRegisterResponseAction.
  scopeOverride?: ResponseActionScope
): ScopedResponseActionsContext => {
  const context: ResponseActionsContextType = useContext(
    ResponseActionsContext
  );
  if (!context.isProvided) {
    throw new Error(
      'useResponseActionsContext must be used within a ResponseActionsProvider'
    );
  }
  const contextScope = useContext(ResponseActionsScopeContext);
  const scope = scopeOverride ?? contextScope;

  return useMemo(
    () => ({
      isProvided: true,
      registerResponseAction: (name, handler) =>
        context.registerResponseAction(scope, name, handler),
      getResponseActionHandler: (name) =>
        context.getResponseActionHandler(scope, name),
    }),
    [context, scope]
  );
};
